import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  IconButton,
  InputAdornment,
  Link
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Close as CloseIcon
} from '@mui/icons-material';

export interface BasicAuthConnectionData {
  domain: string;
  apiKey: string;
}

export interface BasicAuthDialogProps {
  open: boolean;
  appName: string;
  onClose: () => void;
  onConnect: (connectionData: BasicAuthConnectionData) => void;
}

const BasicAuthConnectionDialog: React.FC<BasicAuthDialogProps> = ({
  open,
  appName,
  onClose,
  onConnect
}) => {
  const [domain, setDomain] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isConnecting, setIsConnecting] = useState(false);

  // Reset form when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setDomain('');
      setApiKey('');
      setErrors({});
      setIsConnecting(false);
      setShowApiKey(false);
    }
  }, [open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate Domain
    if (!domain.trim()) {
      newErrors.domain = 'Domain is required';
    } else {
      // Basic domain validation (should be a valid subdomain or domain)
      const domainPattern = /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}$|^[a-zA-Z0-9-]+$/;
      if (!domainPattern.test(domain.trim())) {
        newErrors.domain = 'Please enter a valid domain (e.g., yourcompany.freshdesk.com or yourcompany)';
      }
    }

    // Validate API Key
    if (!apiKey.trim()) {
      newErrors.apiKey = 'API Key is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConnect = async () => {
    if (!validateForm()) {
      return;
    }

    setIsConnecting(true);

    try {
      const connectionData: BasicAuthConnectionData = {
        domain: domain.trim(),
        apiKey: apiKey.trim()
      };

      await onConnect(connectionData);
    } catch (error) {
      console.error('Error connecting:', error);
      setErrors({ general: 'Failed to connect. Please check your credentials and try again.' });
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Connect {appName}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {errors.general && (
            <Alert severity="error">{errors.general}</Alert>
          )}

          <TextField
            label="Domain"
            placeholder="yourcompany.freshdesk.com or yourcompany"
            value={domain}
            onChange={(e) => {
              setDomain(e.target.value);
              if (errors.domain) {
                setErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors.domain;
                  return newErrors;
                });
              }
            }}
            error={!!errors.domain}
            helperText={errors.domain || 'Enter your Freshdesk domain (e.g., yourcompany.freshdesk.com)'}
            fullWidth
            required
          />

          <TextField
            label="API Key"
            type={showApiKey ? 'text' : 'password'}
            placeholder="Enter your API key"
            value={apiKey}
            onChange={(e) => {
              setApiKey(e.target.value);
              if (errors.apiKey) {
                setErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors.apiKey;
                  return newErrors;
                });
              }
            }}
            error={!!errors.apiKey}
            helperText={
              errors.apiKey || (
                <span>
                  Enter your Freshdesk API key.{' '}
                  <Link
                    href="https://support.freshdesk.com/support/solutions/articles/215517-how-to-find-your-api-key"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                  >
                    Get your API key
                  </Link>
                </span>
              )
            }
            fullWidth
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowApiKey(!showApiKey)}
                    edge="end"
                    size="small"
                  >
                    {showApiKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isConnecting}>
          Cancel
        </Button>
        <Button
          onClick={handleConnect}
          variant="contained"
          disabled={isConnecting}
        >
          {isConnecting ? 'Connecting...' : 'Connect'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BasicAuthConnectionDialog;

